import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { Dispatch } from 'redux';

import { getCreditLinesForGiver, requestHandler } from '../../api';
import {
  CreditLineByProduct,
  CreditLineForGiverResponse,
  GetCreditLinesForGiverResponse,
} from './types';

const defaultCreditLinesForGiverResponse: GetCreditLinesForGiverResponse = {
  data: [],
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
};

export const useCreditLineRequests = (dispatch: Dispatch, query: string) =>
  useQuery({
    queryKey: ['creditLine', 'requests', query],
    queryFn: async () => {
      const response = await getCreditLinesForGiver(dispatch, query);
      if (response) return response as GetCreditLinesForGiverResponse;
      return defaultCreditLinesForGiverResponse;
    },
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });

export const useCreditLineForGiver = (dispatch: Dispatch, id: number) =>
  useQuery({
    queryKey: ['creditLine', 'giver', id],
    queryFn: async () => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/creditLine/giver/${id}`,
        dispatch,
      });
      if (response) return response.data as CreditLineForGiverResponse;
      return undefined;
    },
    enabled: !!id,
    retry: 1,
    staleTime: 1000 * 60 * 2,
  });

export const useCreditLineAssignations = (dispatch: Dispatch, id: number) =>
  useQuery({
    queryKey: ['creditLine', 'assignations', id],
    queryFn: async () => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/creditLine/${id}/assignations`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    enabled: !!id,
    retry: 1,
    staleTime: 1000 * 60 * 2,
  });

export const getCreditLineApprovals = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/${id}/approval`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCreditLineByProduct = (
  dispatch: Dispatch,
  businessIdentifier?: string,
  product?: string,
) =>
  useQuery({
    queryKey: ['creditLine', 'product', businessIdentifier, product],
    queryFn: async (): Promise<CreditLineByProduct | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/creditLine/express/${businessIdentifier}/product/${product}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    enabled: !!businessIdentifier && !!product,
    retry: 1,
  });

export const createCreditLineExpress = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['create', 'creditLineExpress'],
    mutationFn: async (data: {
      businessIdentifier: string;
      productType: string;
    }) => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/creditLine/express`,
        data,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          'creditLine',
          'product',
          variables.businessIdentifier,
          variables.productType,
        ],
      });
    },
  });
};

export const postCreditLineExpress = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/creditLine/express`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getAvailableCreditLineProducts = async (
  dispatch: Dispatch,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/availableProducts/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const assignSgrDefaultConcentration = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['assign', 'sgr', 'concentrations'],
    mutationFn: async (data: { id?: number; subProductId: string }) => {
      const { id, subProductId } = data;
      if (!id)
        showAlert(dispatch, 'error', 'Error al asignar la concentración');
      const response = await requestHandler({
        method: 'patch',
        url: `internal/creditLine/${id}/sgr/concentrations/${subProductId}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: () => {
      showAlert(dispatch, 'success', 'Concentración creada con éxito');
      void queryClient.invalidateQueries({
        queryKey: ['creditLine', 'product'],
      });
    },
  });
};
